import { post } from '../../../../infra/rest';
import { ChatListAtom, ChatFilterSlaStatus, ChatStatuses, CustomFieldsData, EntityTypes } from '../typings';
import { BulkChatAction } from '../typings/bulk-action-progress';
import { CustomFieldCondition } from '../components/chat-list-section/custom-field-filter/typings';
import { serializeConditionsForWire } from '../components/chat-list-section/custom-field-filter/utils';

export interface BulkActionParams {
  userId?: string;
  teamId?: string;
  tagIds?: string[];
  groupIds?: string[];
  isDone?: boolean;
  markChatRead?: boolean;
}

export interface BulkActionPayload {
  filter: ChatListAtom;
  excludedChatIds: string[];
  action: BulkChatAction;
  actionParams?: BulkActionParams;
  customFieldsData?: CustomFieldsData;
}

export interface BulkActionResponse {
  operationId: string;
  affectedCount: number;
}

export interface BulkActionFilter {
  status: ChatStatuses;
  slaStatus: ChatFilterSlaStatus | null;
  tags: string[];
  tagIds: string[];
  assignedUserId?: string;
  integrationIds?: string[];
  customFields?: Array<{ id: string; value: string; type?: string; entityType?: EntityTypes }>;
  customFields_v3?: CustomFieldCondition[];
}

export function buildBulkActionFilter(filter: ChatListAtom, customFieldsData?: CustomFieldsData): BulkActionFilter {
  const customFieldIdTypeMap = new Map<string, { type: string; entityType?: EntityTypes }>();
  (customFieldsData?.customFields ?? []).forEach((cf) => {
    customFieldIdTypeMap.set(cf.id, { type: cf.type, entityType: cf.entityType });
  });
  (customFieldsData?.internalFields ?? []).forEach((cf) => {
    customFieldIdTypeMap.set(cf.id, { type: cf.type, entityType: cf.entityType });
  });

  const mergedCustomList: { [key: string]: string } = { ...(filter.customList ?? {}) };
  if (filter.leadCustomFieldIdList?.listId) {
    mergedCustomList[filter.leadCustomFieldIdList.listId] = filter.leadCustomFieldIdList.fieldId;
  }

  const enrichedCustomFields = Object.entries(mergedCustomList)
    .filter(([id, value]) => id && value)
    .map(([id, value]) => ({
      id,
      value,
      type: customFieldIdTypeMap.get(id)?.type,
      entityType: customFieldIdTypeMap.get(id)?.entityType,
    }));

  const resolvedAssignedUserId =
    filter.assignedUserId && filter.assignedUserId !== 'null' ? filter.assignedUserId : undefined;

  const completeConditions = serializeConditionsForWire(filter.customFieldConditions ?? []);

  return {
    status: filter.status,
    slaStatus: filter.slaStatus === 'non-breached' ? null : filter.slaStatus,
    tags: filter.tags,
    tagIds: filter.tagIds,
    ...(resolvedAssignedUserId !== undefined ? { assignedUserId: resolvedAssignedUserId } : {}),
    ...(filter.waba?.length > 0 ? { integrationIds: filter.waba } : {}),
    ...(enrichedCustomFields.length > 0 ? { customFields: enrichedCustomFields } : {}),
    ...(completeConditions.length > 0 ? { customFields_v3: completeConditions } : {}),
  };
}

export const bulkActionOnChats = async (payload: BulkActionPayload): Promise<BulkActionResponse> => {
  const { filter, customFieldsData, ...rest } = payload;
  return post<BulkActionResponse>(
    '/v2/conversation/bulk-action',
    {
      body: JSON.stringify({ ...rest, filter: buildBulkActionFilter(filter, customFieldsData) }),
    },
    true
  );
};
